import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Send } from 'lucide-react';
import { api } from '@/lib/api';
import { useConfirm } from '@/components/ui/confirm';
import { usePermission } from '@/Hooks/usePermission';
import { useBranches } from '@/Pages/Operations/shared';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import ApplyTemplateDialog from './ApplyTemplateDialog';
import ShiftDialog from './ShiftDialog';
import {
  addDaysIso,
  apiError,
  clientOverlapWarnings,
  datesBetweenIso,
  employeeName,
  formatWallTime,
  fmtHours,
  isoToday,
  startOfMonthIso,
  startOfWeekIso,
  endOfMonthIso,
  weekdayKey,
  type EmployeeRef,
  type RoleRef,
  type Roster,
  type Schedule,
  type ScheduledShift,
  type ShiftTemplate,
} from './schedulingLib';

type View = 'week' | 'month';

function rangeFor(view: View, anchor: string): { from: string; to: string } {
  if (view === 'week') return { from: startOfWeekIso(anchor), to: addDaysIso(startOfWeekIso(anchor), 6) };
  return { from: startOfMonthIso(anchor), to: endOfMonthIso(anchor) };
}

/**
 * The roster — the single largest gap this pass closes (ADR-0008 §5). A
 * manager picks a branch and a week or month, sees every shift on it grouped
 * by day, and builds the period by adding shifts one at a time or stamping a
 * template across several dates.
 *
 * Conflicts surface at TWO points, deliberately: a soft, client-side overlap
 * chip on the grid the moment two of an employee's shifts are both visible
 * (`clientOverlapWarnings` — advisory only, never blocks anything), and the
 * server's own answer inside `ShiftDialog` the moment a save is attempted,
 * which is the one that is actually authoritative. Neither waits for
 * publish: a DRAFT period is exactly where a conflict is cheap to fix.
 *
 * Publishing is `scheduling.publish`, a key `all('scheduling')` grants and a
 * bare `scheduling.manage` deliberately does not (see `roles.ts` — a Shift
 * Manager builds the rota but a Branch Manager is the one who sends it out).
 * It notifies every affected employee in one act, which is why the confirm
 * dialog states that in the operator's own terms before the click rather
 * than after it.
 */
export default function RosterTab() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const confirmDialog = useConfirm();
  const branches = useBranches();
  const canManage = can('scheduling.manage');
  const canPublish = can('scheduling.publish');

  const [branchId, setBranchId] = useState('');
  const [view, setView] = useState<View>('week');
  const [anchor, setAnchor] = useState(isoToday());

  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [roster, setRoster] = useState<Roster | null>(null);
  const [scheduleMissing, setScheduleMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [creating, setCreating] = useState(false);

  const [employees, setEmployees] = useState<EmployeeRef[]>([]);
  const [roles, setRoles] = useState<RoleRef[]>([]);
  const [templates, setTemplates] = useState<ShiftTemplate[]>([]);

  const [shiftDialog, setShiftDialog] = useState<{ date: string; shift: ScheduledShift | null } | null>(
    null,
  );
  const [applyOpen, setApplyOpen] = useState(false);

  useEffect(() => {
    if (!branchId && branches.length > 0) setBranchId(branches[0].id);
  }, [branchId, branches]);

  const { from, to } = rangeFor(view, anchor);
  const dates = useMemo(() => datesBetweenIso(from, to), [from, to]);

  const loadSupportingData = useCallback(() => {
    if (!branchId) return;
    api
      .get('/employees', { params: { branchId, status: 'ACTIVE', limit: 200 } })
      .then(({ data }) => setEmployees((data?.data ?? []) as EmployeeRef[]))
      .catch(() => setEmployees([]));
    if (can('roles.view')) {
      api
        .get('/roles', { params: { limit: 200 } })
        .then(({ data }) => setRoles((data?.data ?? data ?? []) as RoleRef[]))
        .catch(() => setRoles([]));
    }
    api
      .get('/scheduling/templates', { params: { branchId } })
      .then(({ data }) => setTemplates((data ?? []) as ShiftTemplate[]))
      .catch(() => setTemplates([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  // Two calls, deliberately not one: `/scheduling/schedules` finds the
  // period (its id and DRAFT/PUBLISHED status — `ScheduleDialog`/`publish`
  // need the id, the badge needs the status), and `/scheduling/roster` reads
  // the shifts already grouped by business date with the server's OWN
  // `scheduledMinutes` per day. Grouping and summing those minutes again
  // here — as this screen briefly did against `GET .../schedules/:id`'s flat
  // shift list — is exactly the client-side re-derivation
  // `SchedulingService.roster`'s own comment warns against: hours feed the
  // labour report below, and a figure that can drift from the server's is a
  // number nobody should be planning a week, or a payroll, around.
  const load = useCallback(() => {
    if (!branchId) return;
    setLoading(true);
    setError(null);
    setScheduleMissing(false);
    api
      .get<{ data: Schedule[] }>('/scheduling/schedules', {
        params: { branchId, from, to, limit: 50 },
      })
      .then(async ({ data }) => {
        const match = (data.data ?? []).find((s) => s.startDate === from && s.endDate === to);
        if (!match) {
          setSchedule(null);
          setRoster(null);
          setScheduleMissing(true);
          return;
        }
        setSchedule(match);
        const { data: rosterData } = await api.get<Roster>('/scheduling/roster', {
          params: { branchId, from, to },
        });
        setRoster(rosterData);
      })
      .catch((e) => setError(apiError(e, t('scheduling.roster.loadFailed'))))
      .finally(() => setLoading(false));
  }, [branchId, from, to, t]);

  useEffect(load, [load]);
  useEffect(loadSupportingData, [loadSupportingData]);

  const createPeriod = async () => {
    setCreating(true);
    try {
      await api.post('/scheduling/schedules', { branchId, startDate: from, endDate: to });
      toast.success(t('scheduling.roster.periodCreated'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('scheduling.roster.periodCreateFailed')));
    } finally {
      setCreating(false);
    }
  };

  const publish = async () => {
    if (!schedule) return;
    // `/scheduling/roster` already excludes CANCELLED shifts server-side, so
    // this is a plain count/enumeration of what is already there — not a
    // duration sum, which is the thing that has to come from the server.
    const active = (roster?.days ?? []).flatMap((d) => d.shifts);
    const employeeIds = new Set(active.map((s) => s.employeeId).filter(Boolean));
    const ok = await confirmDialog.confirm({
      title: t('scheduling.roster.publishConfirmTitle'),
      body: t('scheduling.roster.publishConfirmBody', {
        shiftCount: active.length,
        employeeCount: employeeIds.size,
      }),
      confirmLabel: t('scheduling.roster.publish'),
    });
    if (!ok) return;
    setPublishing(true);
    try {
      await api.post(`/scheduling/schedules/${schedule.id}/publish`);
      toast.success(t('scheduling.roster.published'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('scheduling.roster.publishFailed')));
    } finally {
      setPublishing(false);
    }
  };

  // Grouped by the server, in `/scheduling/roster`'s own response — not
  // re-derived here. `RosterDay.scheduledMinutes` is read directly below
  // rather than reduced from `shifts`, for the same reason.
  const rosterByDate = useMemo(() => {
    const map = new Map<string, Roster['days'][number]>();
    for (const day of roster?.days ?? []) map.set(day.date, day);
    return map;
  }, [roster]);

  const overlapping = useMemo(
    () => clientOverlapWarnings((roster?.days ?? []).flatMap((d) => d.shifts)),
    [roster],
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-end justify-between gap-3 p-4">
          <div className="flex flex-wrap items-end gap-3">
            {branches.length > 0 && (
              <Field label={t('scheduling.roster.branch')}>
                <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <Field label={t('scheduling.roster.view')}>
              <Select value={view} onChange={(e) => setView(e.target.value as View)}>
                <option value="week">{t('scheduling.roster.weekly')}</option>
                <option value="month">{t('scheduling.roster.monthly')}</option>
              </Select>
            </Field>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAnchor(addDaysIso(anchor, view === 'week' ? -7 : -30))}
              >
                {t('common.previous')}
              </Button>
              <span className="px-1 text-sm font-medium tabular-nums">
                {from} – {to}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAnchor(addDaysIso(anchor, view === 'week' ? 7 : 30))}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>

          {schedule && (
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={toneForStatus(schedule.status)}>
                {t(`scheduling.scheduleStatus.${schedule.status}`)}
              </Badge>
              {canManage && templates.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => setApplyOpen(true)}>
                  {t('scheduling.applyTemplate.title')}
                </Button>
              )}
              {schedule.status === 'DRAFT' && canPublish && (
                <Button size="sm" disabled={publishing} onClick={() => void publish()}>
                  <Send className="h-4 w-4" />
                  {t('scheduling.roster.publish')}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : scheduleMissing ? (
        canManage ? (
          <EmptyState
            title={t('scheduling.roster.noPeriod')}
            hint={t('scheduling.roster.noPeriodHint')}
            action={
              <Button size="sm" disabled={creating} onClick={() => void createPeriod()}>
                <Plus className="h-4 w-4" />
                {t('scheduling.roster.createPeriod')}
              </Button>
            }
          />
        ) : (
          <EmptyState title={t('scheduling.roster.noPeriod')} hint={t('scheduling.roster.noPeriodViewerHint')} />
        )
      ) : (
        <div className="space-y-2">
          {dates.map((date) => {
            const day = rosterByDate.get(date);
            const dayShifts = day?.shifts ?? [];
            const minutes = day?.scheduledMinutes ?? 0;
            return (
              <Card key={date}>
                <CardContent className="flex flex-wrap items-start gap-3 p-3">
                  <div className="w-24 shrink-0 pt-1.5">
                    <p className="text-sm font-semibold">{t(weekdayKey(date))}</p>
                    <p className="text-xs text-ink-muted tabular-nums">{date}</p>
                    {dayShifts.length > 0 && (
                      <p className="text-xs text-ink-muted tabular-nums">{fmtHours(minutes)}</p>
                    )}
                  </div>
                  <div className="flex flex-1 flex-wrap gap-1.5">
                    {dayShifts.length === 0 && (
                      <span className="py-1.5 text-xs text-ink-muted">{t('scheduling.roster.noShifts')}</span>
                    )}
                    {dayShifts.map((shift) => (
                      <button
                        key={shift.id}
                        onClick={() => setShiftDialog({ date, shift })}
                        className={`flex flex-col items-start rounded-lg border px-2.5 py-1.5 text-start text-xs hover:bg-hover ${
                          overlapping.has(shift.id) ? 'border-red-400' : 'border-line-strong'
                        }`}
                      >
                        <span className="font-medium tabular-nums">
                          {formatWallTime(shift.startsAt, shift.timezone, i18n.language)}–
                          {formatWallTime(shift.endsAt, shift.timezone, i18n.language)}
                        </span>
                        <span>
                          {employeeName(shift.employee) ?? (
                            <Badge tone="info">{t('scheduling.shift.openShift')}</Badge>
                          )}
                        </span>
                        {shift.role && <span className="text-ink-muted">{shift.role.name}</span>}
                        {overlapping.has(shift.id) && (
                          <Badge tone="danger" className="mt-1">
                            {t('scheduling.shift.overlapWarning')}
                          </Badge>
                        )}
                      </button>
                    ))}
                    {canManage && schedule && (
                      <button
                        onClick={() => setShiftDialog({ date, shift: null })}
                        className="flex items-center gap-1 rounded-lg border border-dashed px-2.5 py-1.5 text-xs text-ink-muted hover:bg-hover"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        {t('scheduling.shift.add')}
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {shiftDialog && schedule && (
        <ShiftDialog
          open
          onClose={() => setShiftDialog(null)}
          scheduleId={schedule.id}
          date={shiftDialog.date}
          shift={shiftDialog.shift}
          employees={employees}
          roles={roles}
          templates={templates}
          canManage={canManage}
          onSaved={load}
        />
      )}

      {schedule && (
        <ApplyTemplateDialog
          open={applyOpen}
          onClose={() => setApplyOpen(false)}
          scheduleId={schedule.id}
          dates={dates}
          templates={templates}
          employees={employees}
          onApplied={load}
        />
      )}
    </div>
  );
}
